import HomeIcon from "@mui/icons-material/Home";
import PeopleIcon from "@mui/icons-material/People";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { Chip, Stack, Typography } from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { usePermissions } from "../hooks/usePermissions";
import { Permission } from "../utils/permissions";

interface LocationViewSelectorProps {
  locationName: string;
  locationId: string;
  locationType: "hangar" | "ramp";
  isSchedulingEnabled?: boolean;
}

export const LocationViewSelector: React.FC<LocationViewSelectorProps> = ({
  locationName,
  locationId,
  locationType,
  isSchedulingEnabled = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission } = usePermissions();

  // Detect current view from URL using React Router
  const getCurrentView = (): string => {
    const path = location.pathname;
    if (path.includes("/schedule")) return "schedule";
    if (path.includes("/reference-")) return "tenants";
    return "current";
  };

  const currentView = getCurrentView();

  // Build navigation options - hide Schedule tab when not enabled
  const viewOptions = React.useMemo(() => {
    const baseRoute = `/${locationType}s`; // "/hangars" or "/ramps"
    const referenceRoute = `/reference-${locationType}s`; // "/reference-hangars" or "/reference-ramps"

    const options = [
      {
        label: t("components.navigation.current"),
        value: "current",
        icon: <HomeIcon />,
        path: `${baseRoute}/${locationId}`,
        disabled: false,
      },
      // Only include Schedule tab when scheduling is enabled
      ...(isSchedulingEnabled ? [{
        label: t("components.navigation.schedule"),
        value: "schedule",
        icon: <ScheduleIcon />,
        path: `${baseRoute}/${locationId}/schedule`,
        disabled: false,
      }] : []),
      // Only include Tenants tab when user has permission to modify tenants
      ...(hasPermission(Permission.MODIFY_TENANTS) ? [{
        label: t("components.navigation.tenants"),
        value: "tenants",
        icon: <PeopleIcon />,
        path: `${referenceRoute}/${locationId}`,
        disabled: false,
      }] : []),
    ];

    return options;
  }, [locationId, locationType, isSchedulingEnabled, hasPermission, t]);

  const handleNavigation = (path: string) => {
    // All visible tabs are now clickable (no disabled tabs)
    navigate(path);
  };

  return (
    <Stack direction="column" spacing={0.5} alignItems="center">
      {/* View selector chips - now on top */}
      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
        {viewOptions.map((option) => (
          <Chip
            key={option.value}
            label={option.label}
            icon={option.icon}
            variant={currentView === option.value ? "filled" : "outlined"}
            size="small"
            clickable={true} // All visible tabs are clickable
            onClick={() => handleNavigation(option.path)}
            sx={{
              fontSize: "0.75rem",
              height: "24px",
              borderRadius: "12px", // Override global square corners for a more modern look
              fontWeight: 500,
              border: "1px solid",
              transition: "all 0.2s ease-in-out",
              "& .MuiChip-icon": {
                fontSize: "14px",
                marginLeft: "4px",
                marginRight: "-2px",
              },
              "& .MuiChip-label": {
                padding: "0 8px",
              },
              // Outlined state (inactive)
              "&.MuiChip-outlined": {
                borderColor: "divider",
                backgroundColor: "transparent",
                color: "text.secondary",
                "&:hover": {
                  borderColor: "primary.main",
                  backgroundColor: "action.hover",
                  color: "text.primary",
                },
                "&:focus": {
                  borderColor: "primary.main",
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: "1px",
                },
              },
              // Filled state (active)
              "&.MuiChip-filled": {
                backgroundColor: "primary.main",
                borderColor: "primary.main",
                color: "primary.contrastText",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "primary.dark",
                  borderColor: "primary.dark",
                },
                "&:focus": {
                  outline: "2px solid",
                  outlineColor: "primary.contrastText",
                  outlineOffset: "1px",
                },
              },
              // Ensure consistent appearance regardless of parent container
              "&.MuiChip-root": {
                minWidth: "fit-content",
                "& .MuiChip-deleteIcon": {
                  display: "none", // Hide delete icon if somehow present
                },
              },
            }}
          />
        ))}
      </Stack>

      {/* Location name (only show if provided) - now below chips */}
      {locationName && (
        <Typography
          variant="h6"
          sx={{
            fontSize: { xs: "1rem", sm: "1.25rem" },
            fontWeight: 600,
          }}
        >
          {locationName}
        </Typography>
      )}
    </Stack>
  );
};
